import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { SectionCard, TierBadge, StatusBadge, Empty } from "@/components/shared";
import { inr, mt, num, fmtDateTime } from "@/lib/format";
import { ArrowLeft, QrCode, CheckCircle2, XCircle, Phone, Mail, MapPin, Building2 } from "lucide-react";
import { toast } from "sonner";

const TABS = ["Overview", "Projects", "Points Ledger", "Redemptions", "Activity", "Digital Card"];

export default function PartnerDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("Overview");
  const [card, setCard] = useState(null);
  const [reason, setReason] = useState("");

  const load = () => api.get(`/partners/${id}`).then((r) => setD(r.data));
  useEffect(() => { load(); api.get(`/partners/${id}/card`).then((r) => setCard(r.data)); }, [id]);

  if (!d) return <div className="text-slate-400 text-sm">Loading…</div>;
  const p = d.partner;

  const verify = async (action) => {
    if (action === "reject" && !reason) { toast.error("Rejection reason is mandatory"); return; }
    try {
      await api.post(`/partners/${id}/verify`, { action, reason: action === "approve" ? (reason || "Verified OK") : reason });
      toast.success(`Partner ${action}d`); setReason(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <button onClick={() => nav("/partners")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-4"><ArrowLeft size={15} /> Back to Partners</button>

      <div className="card-surface p-6 mb-6 fade-up" data-testid="partner-360-header">
        <div className="flex flex-wrap items-start gap-4 justify-between">
          <div className="flex items-start gap-4">
            <div className="h-16 w-16 rounded-xl grid place-items-center text-xl font-extrabold text-white" style={{ background: "var(--navy-800)" }}>
              {p.name.split(" ").map((x) => x[0]).slice(0, 2).join("")}
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-2xl font-extrabold text-slate-900">{p.name}</h1>
                <TierBadge tier={p.tier} /><StatusBadge status={p.status} />
              </div>
              <p className="font-mono text-sm text-[var(--copper)] font-semibold mt-0.5">{p.partner_id}</p>
              <div className="flex flex-wrap gap-4 mt-2 text-xs text-slate-500">
                <span className="flex items-center gap-1"><Building2 size={13} /> {p.partner_type}</span>
                <span className="flex items-center gap-1"><Phone size={13} /> {p.mobile}</span>
                <span className="flex items-center gap-1"><Mail size={13} /> {p.email || "—"}</span>
                <span className="flex items-center gap-1"><MapPin size={13} /> {p.city}</span>
              </div>
            </div>
          </div>
          {p.status === "pending_verification" && (
            <div className="flex flex-col gap-2" data-testid="verify-actions">
              <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Decision reason…" className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm w-56" data-testid="verify-reason" />
              <div className="flex gap-2">
                <button data-testid="approve-partner" onClick={() => verify("approve")} className="flex-1 flex items-center justify-center gap-1.5 bg-emerald-600 text-white text-sm font-semibold px-3 py-1.5 rounded-lg"><CheckCircle2 size={15} /> Approve</button>
                <button data-testid="reject-partner" onClick={() => verify("reject")} className="flex-1 flex items-center justify-center gap-1.5 bg-red-600 text-white text-sm font-semibold px-3 py-1.5 rounded-lg"><XCircle size={15} /> Reject</button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6 stagger">
        {[["Lifetime Points", num(d.metrics.lifetime_points)], ["Balance", num(d.metrics.points_balance)],
          ["Pending", num(d.metrics.points_pending)], ["Projects", d.metrics.projects], ["Supplied", mt(d.metrics.supplied_mt)]].map(([l, v]) => (
          <div key={l} className="card-surface p-4"><p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{l}</p><p className="text-xl font-extrabold font-mono mt-1">{v}</p></div>
        ))}
      </div>

      <div className="flex gap-1 border-b border-slate-200 mb-5 overflow-x-auto">
        {TABS.map((t) => (
          <button key={t} data-testid={`tab-${t.replace(/ /g, "-").toLowerCase()}`} onClick={() => setTab(t)}
            className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 -mb-px ${tab === t ? "border-[var(--copper)] text-slate-900" : "border-transparent text-slate-500 hover:text-slate-700"}`}>{t}</button>
        ))}
      </div>

      {tab === "Overview" && (
        <div className="grid lg:grid-cols-2 gap-6">
          <SectionCard title="Profile Details">
            <dl className="text-sm divide-y divide-slate-50">
              {[["Company", p.company], ["GST", p.gst], ["PAN", p.pan], ["Specialization", p.specialization],
                ["Address", p.address], ["Verified By", p.verified_by], ["Consent", `${p.consent_version} · ${p.consent_at ? "captured" : "—"}`],
                ["Reject Reason", p.reject_reason]].map(([k, v]) => v ? (
                <div key={k} className="flex justify-between py-2"><dt className="text-slate-500">{k}</dt><dd className="text-slate-800 font-medium text-right max-w-[60%]">{v}</dd></div>
              ) : null)}
            </dl>
          </SectionCard>
          <SectionCard title="Recent Activity">
            {d.activities.length === 0 ? <Empty /> : d.activities.slice(0, 6).map((a) => (
              <div key={a.id} className="flex gap-3 py-2 border-b border-slate-50 last:border-0">
                <span className="h-2 w-2 rounded-full bg-[var(--copper)] mt-1.5" />
                <div><p className="text-sm text-slate-700 capitalize">{a.type}: {a.note}</p><p className="text-xs text-slate-400">{fmtDateTime(a.timestamp)}</p></div>
              </div>
            ))}
          </SectionCard>
        </div>
      )}

      {tab === "Projects" && (
        <SectionCard className="!p-0">
          <Tbl head={["Project ID", "MT", "Value", "Payout Eligible", "Verified"]} empty={!d.recommendations.length}>
            {d.recommendations.map((r) => (
              <tr key={r.id} className="border-b border-slate-50 hover:bg-slate-50 cursor-pointer" onClick={() => nav(`/projects/${r.master_id}`)}>
                <td className="px-5 py-3 font-mono text-xs">{r.project_id}</td>
                <td className="px-5 py-3 font-mono">{mt(r.tonnage_mt)}</td>
                <td className="px-5 py-3 font-mono">{inr(r.est_value_inr)}</td>
                <td className="px-5 py-3">{r.payout_eligible ? <span className="text-emerald-600 text-xs font-semibold">Eligible (1st recommender)</span> : <span className="text-slate-400 text-xs">Attribution only</span>}</td>
                <td className="px-5 py-3">{r.verified ? <StatusBadge status="verified" /> : <StatusBadge status="pending" />}</td>
              </tr>
            ))}
          </Tbl>
        </SectionCard>
      )}

      {tab === "Points Ledger" && (
        <SectionCard className="!p-0">
          <Tbl head={["Type", "Points", "Status", "Reason", "Date"]} empty={!d.ledger.length}>
            {d.ledger.map((l) => (
              <tr key={l.id} className="border-b border-slate-50">
                <td className="px-5 py-3 capitalize">{l.type}</td>
                <td className={`px-5 py-3 font-mono font-semibold ${l.type === "burn" || l.type === "reversal" ? "text-red-600" : "text-emerald-600"}`}>{l.type === "burn" || l.type === "reversal" ? "−" : "+"}{num(l.points)}</td>
                <td className="px-5 py-3"><StatusBadge status={l.status} /></td>
                <td className="px-5 py-3 text-slate-600 text-xs">{l.reason}</td>
                <td className="px-5 py-3 text-xs text-slate-400">{fmtDateTime(l.timestamp)}</td>
              </tr>
            ))}
          </Tbl>
        </SectionCard>
      )}

      {tab === "Redemptions" && (
        <SectionCard className="!p-0">
          <Tbl head={["Reward", "Points", "Status", "Date"]} empty={!d.redemptions.length}>
            {d.redemptions.map((r) => (
              <tr key={r.id} className="border-b border-slate-50">
                <td className="px-5 py-3">{r.reward_name}</td><td className="px-5 py-3 font-mono">{num(r.points)}</td>
                <td className="px-5 py-3"><StatusBadge status={r.status} /></td><td className="px-5 py-3 text-xs text-slate-400">{fmtDateTime(r.timestamp)}</td>
              </tr>
            ))}
          </Tbl>
        </SectionCard>
      )}

      {tab === "Activity" && (
        <SectionCard>
          {d.activities.length === 0 ? <Empty /> : d.activities.map((a) => (
            <div key={a.id} className="flex gap-3 py-2.5 border-b border-slate-50 last:border-0">
              <span className="h-2 w-2 rounded-full bg-[var(--copper)] mt-1.5" />
              <div><p className="text-sm text-slate-700 capitalize"><b>{a.type}</b>: {a.note}</p><p className="text-xs text-slate-400">{a.by} · {fmtDateTime(a.timestamp)}</p></div>
            </div>
          ))}
        </SectionCard>
      )}

      {tab === "Digital Card" && card && (
        <div className="max-w-md">
          <div className="id-card-shine rounded-2xl p-6 text-white copper-glow" data-testid="digital-id-card">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-slate-300">INDIGO Partner</p>
                <p className="font-display text-xl font-bold mt-1">{card.name}</p>
                <p className="text-xs text-slate-300">{card.partner_type} · {card.city}</p>
              </div>
              <div className="bg-white p-1.5 rounded-lg"><QrGrid payload={card.qr_payload} /></div>
            </div>
            <div className="mt-8 flex items-end justify-between">
              <div>
                <p className="font-mono text-lg font-bold tracking-wide">{card.partner_id}</p>
                <p className="text-[10px] text-slate-300 mt-1">{card.company}</p>
              </div>
              <TierBadge tier={card.tier} />
            </div>
          </div>
          <p className="text-xs text-slate-400 mt-3 flex items-center gap-1"><QrCode size={13} /> Scan QR to verify partner authenticity.</p>
        </div>
      )}
    </div>
  );
}

function Tbl({ head, children, empty }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">{head.map((h) => <th key={h} className="px-5 py-3">{h}</th>)}</tr></thead>
        <tbody>{children}</tbody>
      </table>
      {empty && <Empty />}
    </div>
  );
}

function QrGrid({ payload }) {
  // deterministic pseudo-QR from payload hash
  let h = 0; for (let i = 0; i < payload.length; i++) h = (h * 31 + payload.charCodeAt(i)) & 0xffffff;
  const cells = [];
  for (let i = 0; i < 49; i++) { h = (h * 1103515245 + 12345) & 0x7fffffff; cells.push(h % 2 === 0); }
  return (
    <div className="grid grid-cols-7 gap-[1px] h-14 w-14">
      {cells.map((on, i) => <div key={i} style={{ background: on ? "#0A1128" : "transparent" }} />)}
    </div>
  );
}
